import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { Link } from 'react-router-dom';

import { ChevronDownIcon, ChevronRightIcon, NoSymbolIcon, PlayIcon } from '@heroicons/react/24/outline';

import { toast } from 'react-toastify';

import { CalendarPlottedEvent, listEnterpriseCalendars } from '../../services/enterpriseCalendarsApi';

import {

  addTasksCalendarTaskEvidence,

  deleteTasksCalendarTaskEvidence,

  fetchTasksCalendarEvidenceImageUrl,

  finishTasksCalendarTask,

  getTasksCalendarCreateContext,

  getTasksCalendarTask,

  startTasksCalendarTask,

  TasksCalendarCreateContext,

  TasksCalendarCreatePayload,

  TasksCalendarObjectiveKind,

  TasksCalendarPlanningObjectiveOption,

  TasksCalendarUpdatePayload,

  TasksCalendarUser,

  updateTasksCalendarTaskEvidence,

} from '../../services/tasksCalendarApi';

import {

  PlanningWorkItem,

  PlanningWorkItemEvidenceFormPayload,

} from '../../services/strategicPlanningApi';

import { PlanningTaskEvidenceSection } from '../strategic-planning/PlanningTaskEvidenceSection';

import {

  PLANNING_FULFILLMENT_OPTIONS,

  formatPlanningFulfillmentResult,

} from '../../types/PlanningTaskStatus';

import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

import { PlanningCancellationReasonField } from '../strategic-planning/PlanningCancellationReasonField';

import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';

import { EditIconButton } from '../ui/EditIconButton';

import { IconActionButton } from '../ui/IconActionButton';

import { RequiredMark } from '../ui/RequiredMark';

import { formatDateBr } from '../../utils/formatDateBr';
import { formatInputDate, normalizeTaskCalendarIds, TASKS_CALENDAR_ID, TASKS_CALENDAR_LABEL } from './calendarUtils';
import { TaskCalendarPicker } from './TaskCalendarPicker';
import { TaskAreaPicker } from '../strategic-planning/TaskAreaPicker';
import { formatCompanyArea } from '../../types/CompanyArea';



type CalendarTasksModalProps = {

  open: boolean;

  mode: 'create' | 'view';

  initialDate?: Date;

  assignableUsers: TasksCalendarUser[];

  defaultOwnerUserId?: string;

  viewingEvent?: CalendarPlottedEvent | null;

  onClose: () => void;

  onSubmit: (payload: TasksCalendarCreatePayload) => Promise<void>;

  onUpdate?: (taskId: string, payload: TasksCalendarUpdatePayload) => Promise<void>;

  onCancel?: (taskId: string, cancellationReason: string) => Promise<void>;

  onExecutionChanged?: () => Promise<void> | void;

  loadCreateContext?: (
    ownerUserId: string,
    taskStartDate?: string,
  ) => Promise<TasksCalendarCreateContext>;

};



const PRIORITIES = [

  { value: 'Low', label: 'Baixa' },

  { value: 'Medium', label: 'Média' },

  { value: 'High', label: 'Alta' },

  { value: 'Critical', label: 'Crítica' },

];



const DESCRIPTION_MAX_LENGTH = 4000;



const TITLE_MAX_LENGTH = 300;



const OBJECTIVE_KINDS: Array<{ value: TasksCalendarObjectiveKind; label: string }> = [

  { value: 'Estrategico', label: 'Estratégico' },

  { value: 'Tatico', label: 'Tático' },

  { value: 'Operacional', label: 'Operacional' },

];



const STATUS_LABELS: Record<string, string> = {

  Planned: 'Planejada',

  InProgress: 'Em andamento',

  Completed: 'Concluída',

  Cancelled: 'Cancelada',

  Overdue: 'Atrasada',

};



const PARENT_KIND_LABELS: Record<string, string> = {

  Meta: 'Meta',

  Marco: 'Marco',

  Tarefa: 'Tarefa',

};



export function CalendarTasksModal({

  open,

  mode,

  initialDate,

  assignableUsers,

  defaultOwnerUserId,

  viewingEvent,

  onClose,

  onSubmit,

  onUpdate,

  onCancel,

  onExecutionChanged,

  loadCreateContext = getTasksCalendarCreateContext,

}: CalendarTasksModalProps) {

  const [title, setTitle] = useState('');

  const [description, setDescription] = useState('');

  const [priority, setPriority] = useState('Medium');

  const [plannedStartDate, setPlannedStartDate] = useState('');

  const [plannedEndDate, setPlannedEndDate] = useState('');

  const [ownerUserId, setOwnerUserId] = useState('');

  const [ownerUserName, setOwnerUserName] = useState('');

  const [calendarIds, setCalendarIds] = useState<string[]>([TASKS_CALENDAR_ID]);
  const [areaIds, setAreaIds] = useState<string[]>([]);

  const [companyCalendars, setCompanyCalendars] = useState<Array<{ id: string; name: string }>>([]);

  const [objectiveKind, setObjectiveKind] = useState<TasksCalendarObjectiveKind>('Operacional');

  const [objectiveId, setObjectiveId] = useState('');

  const [parentWorkItemId, setParentWorkItemId] = useState('');

  const [createContext, setCreateContext] = useState<TasksCalendarCreateContext | null>(null);

  const [loadingContext, setLoadingContext] = useState(false);

  const [contextError, setContextError] = useState(false);

  const [saving, setSaving] = useState(false);

  const [formError, setFormError] = useState('');

  const [detailPanel, setDetailPanel] = useState<'view' | 'edit' | 'cancel'>('view');

  const [cancellationReason, setCancellationReason] = useState('');

  const [executionTask, setExecutionTask] = useState<PlanningWorkItem | null>(null);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const [fulfillmentResult, setFulfillmentResult] = useState('NotFulfilled');

  const executionLoadSeq = useRef(0);



  const taskStatus = executionTask?.status || viewingEvent?.taskStatus || '';

  const executionFinished = Boolean(executionTask?.executionFinishedAt);

  const isTerminal = taskStatus === 'Completed' || taskStatus === 'Cancelled' || executionFinished;

  const canStartExecution =
    !isTerminal
    && Boolean(ownerUserId || executionTask?.ownerUserId)
    && (taskStatus === 'Planned' || taskStatus === 'Overdue');

  const canFinishExecution =
    !executionFinished
    && taskStatus !== 'Completed'
    && taskStatus !== 'Cancelled'
    && (taskStatus === 'InProgress' || taskStatus === 'Overdue')
    && executionTask?.kind !== 'Meta';

  const canEdit = Boolean(onUpdate) && !isTerminal;
  const canCancelTask = Boolean(onCancel) && !isTerminal;



  useEffect(() => {

    if (!open) return;



    setDetailPanel('view');

    setCancellationReason('');

    setFormError('');



    if (mode === 'view' && viewingEvent) {

      setTitle(viewingEvent.taskTitle ?? viewingEvent.name);

      setDescription(viewingEvent.description ?? '');

      setPriority(viewingEvent.taskPriority ?? 'Medium');

      setPlannedStartDate(viewingEvent.eventDate.slice(0, 10));

      setPlannedEndDate(viewingEvent.taskEndDate?.slice(0, 10) ?? viewingEvent.eventDate.slice(0, 10));

      setOwnerUserId(viewingEvent.ownerUserId ?? '');

      setOwnerUserName(viewingEvent.ownerUserName ?? '');

      setCalendarIds(normalizeTaskCalendarIds(viewingEvent.taskCalendarIds));
      setAreaIds(viewingEvent.taskAreas ?? []);

      return;

    }



    const dateLabel = initialDate ? formatInputDate(initialDate) : formatInputDate(new Date());

    setTitle('');

    setDescription('');

    setPriority('Medium');

    setPlannedStartDate(dateLabel);

    setPlannedEndDate(dateLabel);

    const defaultUser =

      assignableUsers.find((user) => user.userId === defaultOwnerUserId)

      ?? assignableUsers.find((user) => user.isSelf);

    setOwnerUserId(defaultUser?.userId ?? '');

    setOwnerUserName(defaultUser?.name ?? '');

    setCalendarIds([TASKS_CALENDAR_ID]);
    setAreaIds([]);

    setParentWorkItemId('');

  }, [open, mode, initialDate, defaultOwnerUserId, assignableUsers, viewingEvent]);

  useEffect(() => {
    if (!open || mode !== 'view') {
      setExecutionTask(null);
      return;
    }

    const taskIdToLoad = viewingEvent?.workItemId;
    if (!taskIdToLoad) {
      setExecutionTask(null);
      return;
    }

    let cancelled = false;
    const seq = ++executionLoadSeq.current;
    setDescriptionExpanded(false);
    setDetailsExpanded(false);
    setFulfillmentResult('NotFulfilled');

    void getTasksCalendarTask(taskIdToLoad)
      .then((task) => {
        if (cancelled || seq !== executionLoadSeq.current) return;
        setExecutionTask(task);
      })
      .catch((error: unknown) => {
        if (cancelled || seq !== executionLoadSeq.current) return;
        setExecutionTask(null);
        toast.error(getRequestErrorMessage(error, 'Não foi possível carregar a execução da tarefa.'));
      });

    return () => {
      cancelled = true;
    };
  }, [open, mode, viewingEvent?.workItemId]);



  useEffect(() => {

    if (!open) return;



    let cancelled = false;

    void listEnterpriseCalendars({ page: 1, pageSize: 100 })

      .then((result) => {

        if (cancelled) return;

        setCompanyCalendars(

          result.items

            .filter((calendar) => calendar.isActive)

            .map((calendar) => ({ id: calendar.id, name: calendar.name })),

        );

      })

      .catch(() => {

        if (!cancelled) setCompanyCalendars([]);

      });



    return () => {

      cancelled = true;

    };

  }, [open]);



  useEffect(() => {

    if (!open || mode !== 'create' || !ownerUserId) {

      setLoadingContext(false);

      setContextError(false);

      return;

    }



    let cancelled = false;

    setLoadingContext(true);

    setContextError(false);



    void loadCreateContext(ownerUserId, plannedStartDate || undefined)

      .then((context) => {

        if (cancelled) return;



        setCreateContext(context);

        setObjectiveKind(context.defaultObjectiveKind);



        const objectivesForKind = context.objectives.filter((objective) => objective.kind === context.defaultObjectiveKind);

        setObjectiveId(objectivesForKind[0]?.id ?? '');

      })

      .catch(() => {

        if (!cancelled) {

          setCreateContext(null);

          setContextError(true);

        }

      })

      .finally(() => {

        if (!cancelled) {

          setLoadingContext(false);

        }

      });



    return () => {

      cancelled = true;

    };

  }, [open, mode, ownerUserId, plannedStartDate, loadCreateContext]);



  const availableObjectives = useMemo((): TasksCalendarPlanningObjectiveOption[] => {

    if (!createContext) return [];

    return createContext.objectives.filter((objective) => objective.kind === objectiveKind);

  }, [createContext, objectiveKind]);



  const filteredParentItems = useMemo(() => {
    if (!createContext) return [];

    return createContext.parentWorkItems.filter((item) => {
      if (objectiveId && item.objectiveId !== objectiveId) return false;
      return true;
    });
  }, [createContext, objectiveId]);



  useEffect(() => {

    if (!createContext || mode !== 'create') return;



    if (!availableObjectives.some((objective) => objective.id === objectiveId)) {

      setObjectiveId(availableObjectives[0]?.id ?? '');

    }

  }, [availableObjectives, objectiveId, createContext, mode]);



  useEffect(() => {

    if (!filteredParentItems.some((item) => item.id === parentWorkItemId)) {

      setParentWorkItemId('');

    }

  }, [filteredParentItems, parentWorkItemId]);



  const calendarOptions = useMemo(

    () => [

      { id: TASKS_CALENDAR_ID, name: `${TASKS_CALENDAR_LABEL} (minha agenda)` },

      ...companyCalendars.filter((calendar) => calendar.id !== TASKS_CALENDAR_ID),

    ],

    [companyCalendars],

  );



  const calendarLabel = calendarOptions

    .filter((option) => calendarIds.includes(option.id))

    .map((option) => option.name)

    .join(', ') || '—';



  if (!open) return null;



  const taskId = viewingEvent?.workItemId ?? viewingEvent?.id ?? '';

  const ownerName =

    ownerUserName

    || viewingEvent?.ownerUserName

    || assignableUsers.find((user) => user.userId === ownerUserId)?.name

    || '—';



  async function handleCreateSubmit(event: FormEvent): Promise<void> {

    event.preventDefault();

    if (mode !== 'create') return;



    setFormError('');



    if (!title.trim()) {

      setFormError('Informe o título da tarefa.');

      return;

    }



    if (!ownerUserId) {

      setFormError('Selecione o responsável pela tarefa.');

      return;

    }



    if (calendarIds.length === 0) {

      setFormError('Selecione ao menos um calendário.');

      return;

    }



    if (!plannedStartDate) {

      setFormError('Informe a data inicial da tarefa.');

      return;

    }



    if (createContext && !createContext.resolvedCycleId) {

      setFormError(

        'Não há ciclo de planejamento para a data inicial. Configure um ciclo que abranja essa data.',

      );

      return;

    }



    if (

      objectiveKind !== 'Operacional'

      && !objectiveId

      && availableObjectives.length === 0

    ) {

      setFormError(

        `Configure um objetivo ${objectiveKind === 'Tatico' ? 'tático' : 'estratégico'} no ciclo antes de criar a tarefa, ou selecione o tipo Operacional.`,

      );

      return;

    }



    setSaving(true);

    try {

      await onSubmit({

        title: title.trim(),

        description: description.trim() || undefined,

        priority,

        plannedStartDate: plannedStartDate || undefined,

        plannedEndDate: plannedEndDate || undefined,

        ownerUserId,

        objectiveKind,

        objectiveId: objectiveId || undefined,

        parentWorkItemId: parentWorkItemId || undefined,

        calendarIds,
        areas: areaIds,

      });

      onClose();

    } catch {

      // Erro exibido pelo handler da página.

    } finally {

      setSaving(false);

    }

  }



  async function handleUpdateSubmit(event: FormEvent): Promise<void> {

    event.preventDefault();

    if (!taskId || !onUpdate) return;



    setFormError('');



    if (!ownerUserId) {

      setFormError('Selecione o responsável pela tarefa.');

      return;

    }



    if (calendarIds.length === 0) {

      setFormError('Selecione ao menos um calendário.');

      return;

    }



    setSaving(true);

    try {

      await onUpdate(taskId, {

        title: title.trim(),

        description: description.trim() || undefined,

        priority,

        plannedStartDate: plannedStartDate || undefined,

        plannedEndDate: plannedEndDate || undefined,

        ownerUserId: ownerUserId || undefined,

        calendarIds,
        areas: areaIds,

      });

      onClose();

    } catch {

      // Erro exibido pelo handler da página.

    } finally {

      setSaving(false);

    }

  }



  async function handleConfirmCancel(): Promise<void> {

    if (!taskId || !onCancel || !cancellationReason.trim()) return;



    setSaving(true);

    try {

      await onCancel(taskId, cancellationReason.trim());

      onClose();

    } catch {

      // Erro exibido pelo handler da página.

    } finally {

      setSaving(false);

    }

  }



  async function persistOwner(nextOwnerUserId: string, nextOwnerUserName: string): Promise<void> {

    if (!taskId || !onUpdate || !title.trim() || !nextOwnerUserId) return;



    const previousOwnerUserId = ownerUserId;

    const previousOwnerUserName = ownerUserName;

    setOwnerUserId(nextOwnerUserId);

    setOwnerUserName(nextOwnerUserName);

    setSaving(true);



    try {

      await onUpdate(taskId, {

        title: title.trim(),

        description: description.trim() || undefined,

        priority,

        plannedStartDate: plannedStartDate || undefined,

        plannedEndDate: plannedEndDate || undefined,

        ownerUserId: nextOwnerUserId,

      });

    } catch {

      setOwnerUserId(previousOwnerUserId);

      setOwnerUserName(previousOwnerUserName);

    } finally {

      setSaving(false);

    }

  }



  const needsConfiguredObjective =

    objectiveKind !== 'Operacional'

    && !objectiveId

    && availableObjectives.length === 0;

  const contextPending =

    mode === 'create'

    && Boolean(ownerUserId)

    && !createContext

    && !contextError

    && !loadingContext;

  const planningBlockedMessage =

    mode !== 'create' || loadingContext || contextPending

      ? null

      : !ownerUserId

        ? 'Selecione o responsável para liberar a criação da tarefa.'

        : contextError || !createContext

          ? 'Não foi possível carregar o planejamento. A tarefa não pode ser criada até que o ciclo seja identificado.'

          : !createContext.resolvedCycleId

            ? 'Não há ciclo de planejamento para a data inicial informada. Configure um ciclo que abranja essa data.'

            : needsConfiguredObjective

              ? `Não há objetivo ${objectiveKind === 'Tatico' ? 'tático' : 'estratégico'} neste ciclo. Cadastre um no planejamento ou selecione o tipo Operacional.`

              : null;



  const canSubmitCreate =

    Boolean(title.trim())

    && Boolean(ownerUserId)

    && Boolean(plannedStartDate)

    && calendarIds.length > 0

    && Boolean(createContext?.resolvedCycleId)

    && !loadingContext

    && !needsConfiguredObjective;



  const canSubmitEdit = Boolean(title.trim()) && Boolean(plannedStartDate) && Boolean(ownerUserId) && calendarIds.length > 0;

  const hasFooterActions =

    mode === 'create'

    || detailPanel !== 'view'

    || canEdit

    || canCancelTask;



  const modalTitle =

    mode === 'create'

      ? 'Nova tarefa'

      : detailPanel === 'edit'

        ? 'Editar tarefa'

        : detailPanel === 'cancel'

          ? 'Cancelar tarefa'

          : 'Detalhes da tarefa';



  function renderEditFields(): JSX.Element {

    return (

      <>

        <label className="block">

          <span className="text-sm font-medium text-slate-700">Título</span>

          <input

            required

            value={title}

            onChange={(event) => setTitle(event.target.value)}

            className="erp-input mt-1 w-full"

            maxLength={TITLE_MAX_LENGTH}

          />

          <span className="mt-1 block text-xs text-slate-500">

            {title.length} / {TITLE_MAX_LENGTH} caracteres

          </span>

        </label>



        <TaskCalendarPicker

          options={calendarOptions}

          selectedIds={calendarIds}

          disabled={saving}

          onChange={setCalendarIds}

        />

        <TaskAreaPicker
          selectedIds={areaIds}
          disabled={saving}
          onChange={setAreaIds}
        />



        <label className="block">

          <span className="text-sm font-medium text-slate-700">Descrição</span>

          <textarea

            value={description}

            onChange={(event) => setDescription(event.target.value)}

            className="erp-input mt-1 w-full"

            rows={3}

            maxLength={DESCRIPTION_MAX_LENGTH}

          />

          <span className="mt-1 block text-xs text-slate-500">

            {description.length} / {DESCRIPTION_MAX_LENGTH} caracteres

          </span>

        </label>



        <label className="block">

          <span className="text-sm font-medium text-slate-700">

            Responsável <RequiredMark />

          </span>

          <p className="mb-2 mt-1 text-xs text-slate-500">

            Clique para buscar pelo nome ou e-mail. A escolha é aplicada ao sair do campo.

          </p>

          <CollaboratorAutocomplete

            valueUserId={ownerUserId || null}

            valueUserName={ownerUserName || null}

            disabled={saving}

            saving={saving}

            onCommit={(user) => {

              setOwnerUserId(user?.userId ?? '');

              setOwnerUserName(user?.name ?? '');

            }}

          />

        </label>



        <div className="grid grid-cols-2 gap-4">

          <label className="block">

            <span className="text-sm font-medium text-slate-700">Início</span>

            <input

              type="date"

              required

              value={plannedStartDate}

              onChange={(event) => setPlannedStartDate(event.target.value)}

              className="erp-input mt-1 w-full"

            />

          </label>

          <label className="block">

            <span className="text-sm font-medium text-slate-700">Fim</span>

            <input

              type="date"

              value={plannedEndDate}

              onChange={(event) => setPlannedEndDate(event.target.value)}

              className="erp-input mt-1 w-full"

              readOnly={
                mode === 'view'
                && (taskStatus === 'InProgress' || taskStatus === 'Overdue')
              }

              disabled={
                mode === 'view'
                && (taskStatus === 'InProgress' || taskStatus === 'Overdue')
              }

              title={
                mode === 'view' && (taskStatus === 'InProgress' || taskStatus === 'Overdue')
                  ? 'Após iniciar a execução, use Estender prazo'
                  : undefined
              }

            />

            {mode === 'view' && (taskStatus === 'InProgress' || taskStatus === 'Overdue') ? (
              <span className="mt-1 block text-xs text-slate-500">
                Após iniciar a execução, use Estender prazo em Minhas tarefas.
              </span>
            ) : null}

          </label>

        </div>



        <label className="block">

          <span className="text-sm font-medium text-slate-700">Prioridade</span>

          <select

            value={priority}

            onChange={(event) => setPriority(event.target.value)}

            className="erp-input mt-1 w-full"

          >

            {PRIORITIES.map((item) => (

              <option key={item.value} value={item.value}>

                {item.label}

              </option>

            ))}

          </select>

        </label>

      </>

    );

  }

  async function reloadExecutionTask(): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    const seq = ++executionLoadSeq.current;
    const task = await getTasksCalendarTask(currentTaskId);
    if (seq === executionLoadSeq.current) setExecutionTask(task);
  }

  async function handleStartExecution(): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    const seq = ++executionLoadSeq.current;
    setSaving(true);
    try {
      const task = await startTasksCalendarTask(currentTaskId);
      if (seq === executionLoadSeq.current) setExecutionTask(task);
      toast.success('Execução iniciada.');
      await onExecutionChanged?.();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível iniciar a execução.'));
      await reloadExecutionTask().catch(() => undefined);
    } finally {
      setSaving(false);
    }
  }

  async function handleFinishExecution(): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    const seq = ++executionLoadSeq.current;
    setSaving(true);
    try {
      const task = await finishTasksCalendarTask(currentTaskId, fulfillmentResult);
      if (seq === executionLoadSeq.current) setExecutionTask(task);
      toast.success('Tarefa finalizada.');
      await onExecutionChanged?.();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível finalizar a tarefa.'));
      await reloadExecutionTask().catch(() => undefined);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddEvidence(payload: PlanningWorkItemEvidenceFormPayload): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    setSaving(true);
    try {
      await addTasksCalendarTaskEvidence(currentTaskId, payload);
      toast.success('Evidência adicionada.');
      await reloadExecutionTask();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível adicionar evidência.'));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateEvidence(
    evidenceId: string,
    payload: PlanningWorkItemEvidenceFormPayload,
  ): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    setSaving(true);
    try {
      await updateTasksCalendarTaskEvidence(currentTaskId, evidenceId, payload);
      toast.success('Evidência atualizada.');
      await reloadExecutionTask();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar a evidência.'));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEvidence(evidenceId: string): Promise<void> {
    const currentTaskId = viewingEvent?.workItemId;
    if (!currentTaskId) return;
    setSaving(true);
    try {
      await deleteTasksCalendarTaskEvidence(currentTaskId, evidenceId);
      toast.success('Evidência excluída.');
      await reloadExecutionTask();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível excluir a evidência.'));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  const detailAreas = (executionTask?.areas?.length ? executionTask.areas : viewingEvent?.taskAreas) ?? [];



  return createPortal(

    <div className="fixed inset-0 z-[100]">

      <div className="absolute inset-0 bg-slate-900/40" aria-hidden />

      <div className="relative flex h-full items-start justify-center overflow-y-auto px-4 py-0">

      <div className="my-0 flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">

        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">

          <div className="min-w-0">

            <h2 className="text-lg font-semibold text-slate-900">{modalTitle}</h2>

            <p className="mt-1 text-sm text-slate-500">Calendário de tarefas</p>

          </div>

          <button
            type="button"
            className="erp-btn-secondary shrink-0 px-2 py-1 text-sm"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
          >
            ✕
          </button>

        </div>



        <div className="min-h-0 flex-1 overflow-y-auto">

        {mode === 'view' && viewingEvent && detailPanel === 'view' ? (

          <div className="space-y-4 px-6 py-5">

            <div>
              <SectionToggle
                expanded={descriptionExpanded}
                controlsId="calendar-task-description"
                onToggle={() => setDescriptionExpanded((current) => !current)}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Título</p>
                <p className="mt-1 text-sm text-slate-900">{title}</p>
              </SectionToggle>
              {descriptionExpanded ? (
                <div id="calendar-task-description" className="mt-3 pl-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Descrição</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                    {description.trim() ? description : 'Sem descrição.'}
                  </p>
                </div>
              ) : null}
            </div>

            {viewingEvent.metaTitle || viewingEvent.marcoTitle ? (
              <div className="rounded-lg border border-primary-200/70 bg-primary-50/80 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Objetivos
                </p>
                <div className="mt-2.5 space-y-2.5 border-t border-primary-200/50 pt-2.5">
                  {viewingEvent.metaTitle ? (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Meta
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-slate-900">
                        {viewingEvent.metaTitle}
                      </p>
                    </div>
                  ) : null}
                  {viewingEvent.marcoTitle ? (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Marco
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-slate-900">
                        {viewingEvent.marcoTitle}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div>

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Responsável</p>

              {canEdit ? (

                <div className="mt-1">

                  <CollaboratorAutocomplete

                    valueUserId={ownerUserId || null}

                    valueUserName={ownerUserName || null}

                    disabled={saving}

                    saving={saving}

                    onCommit={(user) => {

                      if (!user) return;

                      void persistOwner(user.userId, user.name);

                    }}

                  />

                </div>

              ) : (

                <p className="mt-1 text-sm text-slate-900">{ownerName}</p>

              )}

            </div>

            {canStartExecution ? (
              <button
                type="button"
                className="erp-btn-primary w-full gap-2"
                disabled={saving}
                onClick={() => void handleStartExecution()}
              >
                <PlayIcon className="h-5 w-5" />
                Iniciar
              </button>
            ) : null}

            {executionTask?.fulfillmentResult ? (
              <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm text-secondary-800">
                Execução: <strong>{formatPlanningFulfillmentResult(executionTask.fulfillmentResult)}</strong>
              </p>
            ) : null}

            {canFinishExecution ? (
              <div className="space-y-2 rounded-xl border border-secondary-200 p-3">
                <p className="text-sm font-semibold text-secondary-800">Resultado da execução</p>
                <label className="block">
                  <select
                    className="erp-input"
                    value={fulfillmentResult}
                    onChange={(event) => setFulfillmentResult(event.target.value)}
                  >
                    {PLANNING_FULFILLMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="erp-btn-primary w-full"
                  disabled={saving}
                  onClick={() => void handleFinishExecution()}
                >
                  Finalizar
                </button>
              </div>
            ) : null}

            {executionTask && viewingEvent.workItemId ? (
              <PlanningTaskEvidenceSection
                taskId={viewingEvent.workItemId}
                evidences={executionTask.evidences ?? []}
                canWrite
                busy={saving}
                loadImageUrl={fetchTasksCalendarEvidenceImageUrl}
                onAdd={handleAddEvidence}
                onUpdate={handleUpdateEvidence}
                onDelete={handleDeleteEvidence}
              />
            ) : null}

            <div>
              <SectionToggle
                expanded={detailsExpanded}
                controlsId="calendar-task-details"
                onToggle={() => setDetailsExpanded((current) => !current)}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Detalhes</p>
              </SectionToggle>
              {detailsExpanded ? (
                <div id="calendar-task-details" className="mt-3 space-y-4 pl-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Calendários</p>
                    <p className="mt-1 text-sm text-slate-900">{calendarLabel}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Áreas</p>
                    <p className="mt-1 text-sm text-slate-900">
                      {detailAreas.length > 0
                        ? detailAreas.map((area) => formatCompanyArea(area)).join(', ')
                        : 'Nenhuma área vinculada'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
                    <p className="mt-1 text-sm text-slate-900">
                      {STATUS_LABELS[taskStatus] ?? taskStatus ?? '—'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Início</p>
                      <p className="mt-1 text-sm text-slate-900">{formatDateBr(plannedStartDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fim</p>
                      <p className="mt-1 text-sm text-slate-900">{formatDateBr(plannedEndDate)}</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {viewingEvent.workItemId ? (

              <Link

                to={`/strategic-planning?tab=tasks&taskId=${viewingEvent.workItemId}`}

                className="inline-flex text-sm font-medium text-primary-600 hover:text-primary-700"

              >

                Abrir no planejamento estratégico

              </Link>

            ) : null}

          </div>

        ) : null}



        {mode === 'view' && viewingEvent && detailPanel === 'edit' ? (

          <form className="space-y-4 px-6 py-5" onSubmit={(event) => void handleUpdateSubmit(event)}>

            {formError ? (

              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">

                {formError}

              </p>

            ) : null}

            {renderEditFields()}

          </form>

        ) : null}



        {mode === 'view' && viewingEvent && detailPanel === 'cancel' ? (

          <div className="space-y-4 px-6 py-5">

            <p className="text-sm text-slate-600">

              Informe o motivo do cancelamento da tarefa <strong>{title}</strong>.

            </p>

            <PlanningCancellationReasonField

              value={cancellationReason}

              onChange={setCancellationReason}

            />

          </div>

        ) : null}



        {mode === 'create' ? (

          <form className="space-y-4 px-6 py-5" onSubmit={(event) => void handleCreateSubmit(event)}>

            {formError ? (

              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">

                {formError}

              </p>

            ) : null}



            {renderEditFields()}



            {loadingContext || contextPending || planningBlockedMessage || createContext?.resolvedCycleId ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">

              {loadingContext || contextPending ? (

                <p className="text-sm text-slate-500">Carregando opções de planejamento...</p>

              ) : null}



              {!loadingContext && !contextPending && planningBlockedMessage ? (

                <p className="text-sm text-amber-700">{planningBlockedMessage}</p>

              ) : null}



              {!loadingContext && createContext?.resolvedCycleName ? (

                <p className="text-sm text-slate-600">

                  Ciclo: <span className="font-medium text-slate-800">{createContext.resolvedCycleName}</span>

                  <span className="mt-1 block text-xs text-slate-500">

                    Definido automaticamente pela data inicial da tarefa.

                  </span>

                </p>

              ) : null}



              {!loadingContext && createContext?.resolvedCycleId ? (

                <>

                  <fieldset>

                    <legend className="text-sm font-medium text-slate-700">Tipo de objetivo</legend>

                    <div className="mt-2 flex flex-wrap gap-4">

                      {OBJECTIVE_KINDS.map((option) => (

                        <label key={option.value} className="inline-flex items-center gap-2 text-sm text-slate-700">

                          <input

                            type="radio"

                            name="objectiveKind"

                            value={option.value}

                            checked={objectiveKind === option.value}

                            onChange={() => setObjectiveKind(option.value)}

                          />

                          {option.label}

                        </label>

                      ))}

                    </div>

                    <span className="mt-1 block text-xs text-slate-500">

                      Padrão: {createContext.defaultObjectiveKind === 'Tatico' ? 'tático' : 'operacional'}

                      {createContext.isOwnerTeamLeader ? ' (gestor)' : ' (colaborador)'}.

                    </span>

                  </fieldset>



                  <label className="block">

                    <span className="text-sm font-medium text-slate-700">

                      Objetivo {objectiveKind === 'Estrategico' ? 'estratégico' : objectiveKind === 'Tatico' ? 'tático' : 'operacional'}

                    </span>

                    <select

                      value={objectiveId}

                      onChange={(event) => setObjectiveId(event.target.value)}

                      className="erp-input mt-1 w-full"

                    >

                      <option value="">Usar objetivo padrão do ciclo</option>

                      {availableObjectives.map((objective) => (

                        <option key={objective.id} value={objective.id}>

                          {objective.title}

                        </option>

                      ))}

                    </select>

                  </label>



                  <label className="block">

                      <span className="text-sm font-medium text-slate-700">

                        Subordinar a meta, marco ou tarefa (opcional)

                      </span>

                      <select

                        value={parentWorkItemId}

                        onChange={(event) => setParentWorkItemId(event.target.value)}

                        className="erp-input mt-1 w-full"

                      >

                        <option value="">Tarefa autônoma no objetivo</option>

                        {filteredParentItems.map((item) => (

                          <option key={item.id} value={item.id}>

                            [{PARENT_KIND_LABELS[item.kind] ?? item.kind}] {item.title}

                          </option>

                        ))}

                      </select>

                    </label>

                </>

              ) : null}

            </div>
            ) : null}

          </form>

        ) : null}



        </div>



        {hasFooterActions ? (
        <div className="erp-btn-actions shrink-0 border-t border-slate-100 px-6 py-4">
            {mode === 'view' && detailPanel !== 'view' ? (
              <button
                type="button"
                className="erp-btn-secondary"
                disabled={saving}
                onClick={() => setDetailPanel('view')}
              >
                Voltar
              </button>
            ) : null}

            {mode === 'view' && detailPanel === 'view' ? (
              <div className="ml-auto flex items-center gap-1">
                {canEdit ? (
                  <EditIconButton
                    label="Editar"
                    disabled={saving}
                    onClick={() => setDetailPanel('edit')}
                  />
                ) : null}
                {canCancelTask ? (
                  <IconActionButton
                    label="Cancelar tarefa"
                    icon={NoSymbolIcon}
                    tone="danger"
                    disabled={saving}
                    onClick={() => setDetailPanel('cancel')}
                  />
                ) : null}
              </div>
            ) : null}



            {mode === 'view' && detailPanel === 'edit' ? (

              <button

                type="button"

                className="erp-btn-primary"

                disabled={saving || !canSubmitEdit}

                onClick={(event) => void handleUpdateSubmit(event as unknown as FormEvent)}

              >

                {saving ? 'Salvando...' : 'Salvar'}

              </button>

            ) : null}



            {mode === 'view' && detailPanel === 'cancel' ? (

              <button

                type="button"

                className="erp-btn-danger"

                disabled={saving || !cancellationReason.trim()}

                onClick={() => void handleConfirmCancel()}

              >

                {saving ? 'Cancelando...' : 'Confirmar cancelamento'}

              </button>

            ) : null}



            {mode === 'create' ? (

              <button

                type="button"

                className="erp-btn-primary"

                disabled={saving || !canSubmitCreate}

                onClick={(event) => void handleCreateSubmit(event as unknown as FormEvent)}

              >

                {saving ? 'Salvando...' : 'Criar tarefa'}

              </button>

            ) : null}

        </div>
        ) : null}

      </div>

      </div>

    </div>,

    document.body,

  );

}

function SectionToggle({
  expanded,
  controlsId,
  onToggle,
  children,
}: {
  expanded: boolean;
  controlsId: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-start gap-2 text-left"
      aria-expanded={expanded}
      aria-controls={controlsId}
      onClick={onToggle}
    >
      {expanded ? (
        <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      ) : (
        <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      )}
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}


